import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  RefreshCw,
  Calendar,
} from "lucide-react";
import { adminApi, AdminApiError } from "../lib/api";

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const methodColors: Record<string, string> = {
  GET: "bg-blue-100 text-blue-700",
  POST: "bg-green-100 text-green-700",
  PUT: "bg-amber-100 text-amber-700",
  PATCH: "bg-orange-100 text-orange-700",
  DELETE: "bg-red-100 text-red-700",
};

const statusColors: Record<string, string> = {
  "4": "bg-amber-100 text-amber-700",
  "5": "bg-red-100 text-red-700",
};

function getStatusColor(code: number): string {
  return statusColors[String(code)[0]] || "bg-slate-100 text-slate-600";
}

function ErrorDetail({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ErrorRow({
  err,
  isExpanded,
  onToggle,
}: {
  err: AdminApiError;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={isExpanded ? "bg-slate-50/80" : ""}>
      <button
        onClick={onToggle}
        className="w-full grid grid-cols-[50px_60px_1fr_1.5fr_120px_90px_24px] gap-3 px-5 py-3 hover:bg-slate-50/50 transition-colors items-center text-left"
      >
        <span
          className={`text-xs font-bold px-2 py-0.5 rounded-full text-center ${getStatusColor(err.statusCode)}`}
        >
          {err.statusCode}
        </span>
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded text-center ${methodColors[err.method] || "bg-slate-100 text-slate-600"}`}
        >
          {err.method}
        </span>
        <span className="text-xs font-mono text-slate-600 truncate">
          {err.path}
        </span>
        <span className="text-xs text-slate-500 truncate">
          {err.errorCode && (
            <span className="font-mono text-red-500 mr-1.5">
              {err.errorCode}
            </span>
          )}
          {err.errorMessage}
        </span>
        <span className="text-xs text-slate-400 truncate">
          {err.userEmail || (
            <span className="text-slate-300">anonymous</span>
          )}
        </span>
        <span className="text-xs text-slate-400 text-right">
          {timeAgo(err.createdAt)}
        </span>
        <ChevronDown
          size={14}
          className={`text-slate-300 transition-transform ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>

      {isExpanded && (
        <div className="px-5 pb-4 pt-1 space-y-3 border-t border-slate-100 ml-5 mr-5">
          <div className="grid grid-cols-2 gap-4">
            <ErrorDetail label="Error Code">
              <span className="text-xs font-mono text-red-600">
                {err.errorCode || "—"}
              </span>
            </ErrorDetail>
            <ErrorDetail label="Timestamp">
              <span className="text-xs text-slate-600">
                {formatTimestamp(err.createdAt)}
              </span>
            </ErrorDetail>
            <ErrorDetail label="User">
              <span className="text-xs text-slate-600">
                {err.userEmail || "anonymous"}
                {err.userId && (
                  <span className="text-slate-400 ml-1.5">
                    (ID: {err.userId})
                  </span>
                )}
              </span>
            </ErrorDetail>
            <ErrorDetail label="Full Path">
              <span className="text-xs font-mono text-slate-600 break-all">
                {err.method} {err.path}
                {err.queryParams && `?${new URLSearchParams(JSON.parse(err.queryParams)).toString()}`}
              </span>
            </ErrorDetail>
          </div>

          <ErrorDetail label="Error Message">
            <p className="text-xs text-slate-600 wrap-break-word">
              {err.errorMessage || "—"}
            </p>
          </ErrorDetail>

          {err.requestBody && (
            <ErrorDetail label="Request Body">
              <pre className="text-[11px] text-slate-600 bg-slate-100 rounded-lg p-3 overflow-x-auto max-h-40 font-mono">
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(err.requestBody), null, 2);
                  } catch {
                    return err.requestBody;
                  }
                })()}
              </pre>
            </ErrorDetail>
          )}

          {err.queryParams && (
            <ErrorDetail label="Query Parameters">
              <pre className="text-[11px] text-slate-600 bg-slate-100 rounded-lg p-3 overflow-x-auto font-mono">
                {(() => {
                  try {
                    return JSON.stringify(
                      JSON.parse(err.queryParams),
                      null,
                      2,
                    );
                  } catch {
                    return err.queryParams;
                  }
                })()}
              </pre>
            </ErrorDetail>
          )}

          {err.stackTrace && (
            <ErrorDetail label="Stack Trace">
              <pre className="text-[11px] text-red-700/80 bg-red-50 rounded-lg p-3 overflow-x-auto max-h-48 font-mono whitespace-pre-wrap">
                {err.stackTrace}
              </pre>
            </ErrorDetail>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApiErrors() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";

  const [errors, setErrors] = useState<AdminApiError[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchErrors = useCallback(
    async (offset = 0, isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const result = await adminApi.errors({
          from: from || undefined,
          to: to || undefined,
          limit: 50,
          offset,
        });
        setErrors(result.data);
        setPagination(result.pagination);
        setExpandedId(null);
      } catch {
        // keep stale data
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [from, to],
  );

  useEffect(() => {
    fetchErrors(0);
  }, [fetchErrors]);

  const handleDateChange = (field: "from" | "to", value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(field, value);
    else params.delete(field);
    setSearchParams(params);
  };

  const handlePage = (direction: "prev" | "next") => {
    const newOffset =
      direction === "next"
        ? pagination.offset + pagination.limit
        : Math.max(0, pagination.offset - pagination.limit);
    fetchErrors(newOffset);
  };

  const pageNum = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit) || 1;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">API Errors</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {pagination.total} error{pagination.total !== 1 ? "s" : ""}{" "}
              {from && to
                ? `from ${formatDateShort(from)} to ${formatDateShort(to)}`
                : "total"}
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchErrors(pagination.offset, true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Date filters */}
      <div className="flex items-center gap-3 mb-6">
        <Calendar size={16} className="text-slate-400" />
        <input
          type="date"
          value={from}
          max={to || undefined}
          onChange={(e) => handleDateChange("from", e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700"
        />
        <span className="text-sm text-slate-400">to</span>
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(e) => handleDateChange("to", e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700"
        />
        {(from || to) && (
          <button
            onClick={() => setSearchParams(new URLSearchParams())}
            className="text-xs text-slate-400 hover:text-slate-600 underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="px-5 py-4 border-b border-slate-100 animate-pulse flex gap-4"
            >
              <div className="h-4 w-12 bg-slate-200 rounded" />
              <div className="h-4 w-14 bg-slate-200 rounded" />
              <div className="h-4 w-48 bg-slate-200 rounded flex-1" />
              <div className="h-4 w-32 bg-slate-100 rounded" />
              <div className="h-4 w-20 bg-slate-100 rounded" />
            </div>
          ))}
        </div>
      ) : errors.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <AlertTriangle size={40} className="text-slate-200 mx-auto mb-3" />
          <h2 className="text-base font-semibold text-slate-500">
            No errors found
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            {from && to
              ? "No errors in this date range."
              : "No API errors recorded yet."}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[50px_60px_1fr_1.5fr_120px_90px_24px] gap-3 px-5 py-3 border-b border-slate-200 bg-slate-50/50">
              <span className="text-[10px] font-bold text-slate-400 uppercase">
                Status
              </span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">
                Method
              </span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">
                Path
              </span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">
                Error
              </span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">
                User
              </span>
              <span className="text-[10px] font-bold text-slate-400 uppercase text-right">
                When
              </span>
              <span />
            </div>

            {/* Rows */}
            <div className="divide-y divide-slate-100">
              {errors.map((err) => (
                <ErrorRow
                  key={err.id}
                  err={err}
                  isExpanded={expandedId === err.id}
                  onToggle={() =>
                    setExpandedId(expandedId === err.id ? null : err.id)
                  }
                />
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-slate-400">
              Showing {pagination.offset + 1}–
              {Math.min(
                pagination.offset + pagination.limit,
                pagination.total,
              )}{" "}
              of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePage("prev")}
                disabled={pagination.offset === 0}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs text-slate-500 font-medium">
                {pageNum} / {totalPages}
              </span>
              <button
                onClick={() => handlePage("next")}
                disabled={!pagination.hasMore}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
